import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import TextField from "components/common/TextField";
import useIsMobile from "components/hooks/useIsMobile";
import {
  Activity,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  CreditCard,
  Flag,
  Plus,
  RefreshCcw,
  Sparkles,
  Star,
  Tag,
  Timer,
  Trash2,
  Undo2,
  UserRoundCheck,
  Users,
  Zap,
} from "lucide-react";
import LinkRouter from "components/navigation/LinkRouter";
import { useSplitInvoicePayout, useUpdateInvoice } from "services/billing";
import {
  Invoice,
  SplitInvoicePayoutRequest,
  UpdateInvoiceRequest,
} from "protogen/billing_service_pb";
import { feeSchedule } from "components/family/utils";
import InvoiceStatusSelect from "components/payments/InvoiceStatusSelect";
import { PartialMessage } from "@bufbuild/protobuf";
import AdvisorSelect from "../../components/creation/AdvisorSelect";
import { formatDateRange, formatPrice, shortDate } from "common/utils";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import InvoiceHistoryFeed from "./InvoiceHistoryFeed";
import FlexBox from "components/common/FlexBox";
import { ReactComponent as FamilyIcon } from "../../icons/Family.svg";

const LightningTaskTimeChip = ({
  stripeMetadata,
}: {
  stripeMetadata: Record<string, string>;
}) => {
  if (!stripeMetadata.realized_minutes && !stripeMetadata.estimated_minutes)
    return null;
  const realized = Number(stripeMetadata.realized_minutes) || 0;
  const estimated = Number(stripeMetadata.estimated_minutes) || 0;
  const isOverEstimate = estimated > 0 && realized > estimated * 1.5;
  const label =
    realized && estimated
      ? `${realized} min (${estimated}m est.)`
      : realized
        ? `${realized} min spent`
        : `${estimated} min estimated`;
  return (
    <Chip
      variant={isOverEstimate ? "orange" : "teal"}
      icon={isOverEstimate ? <CircleAlert size={16} /> : <Timer size={16} />}
      label={label}
      size="small"
    />
  );
};

const InvoiceFlags = ({ invoice }: { invoice: Invoice }) => {
  const isExtraHours = invoice.transactionType === "extra-hours";
  const isLightningTask = invoice.transactionType === "lightning-task";
  const isSplitPayout = invoice.transactionType === "split-payout";
  const hasActiveFlags = invoice.flags?.hasActiveFlags;
  const stripeMetadata = invoice.stripeMetadataJson
    ? JSON.parse(invoice.stripeMetadataJson)
    : {};

  const subpaymentCount = Math.max(invoice.payoutRecipients.length - 1, 0);
  const hasFlags =
    hasActiveFlags ||
    isExtraHours ||
    isLightningTask ||
    isSplitPayout ||
    subpaymentCount > 0 ||
    // This will almost always be true, we might just want to drop this if.
    invoice.planUtilization?.planHoursAllocated ||
    !invoice.isAdvisorPayable ||
    invoice.flags?.isAdvisorNew ||
    invoice.flags?.isRecentRematch ||
    invoice.flags?.advisorIsWrong ||
    invoice.flags?.isPaymentRefunded ||
    invoice.serviceMonth <= 1 ||
    invoice.subtotalCents > invoice.invoiceAmountCents ||
    invoice.flags?.isBillingChange;

  if (!hasFlags) return null;
  const utilization = invoice.planUtilization?.planHoursAllocated
    ? (invoice.planUtilization?.periodHoursUtilized || 0) /
      (invoice.planUtilization?.planHoursAllocated || 1)
    : null;

  return (
    <Box
      sx={{ display: "flex", gap: "8px", padding: "8px 0 0", flexWrap: "wrap" }}
    >
      {/* Active flags - displayed first and most prominently */}
      {hasActiveFlags && (
        <Chip
          variant="red"
          icon={<Flag size={16} />}
          label="Active Flag"
          size="small"
          sx={{
            fontWeight: 600,
            border: "2px solid #DC2626",
          }}
        />
      )}
      {invoice.flags?.isPaymentRefunded && (
        <Chip
          variant="red"
          icon={<Undo2 size={16} />}
          label="Refunded"
          size="small"
        />
      )}
      {isSplitPayout && (
        <Chip
          variant="teal"
          icon={<Users size={16} />}
          label="Subpayment"
          size="small"
        />
      )}
      {subpaymentCount > 0 && (
        <Tooltip
          title={invoice.payoutRecipients
            .map(
              (r, i) =>
                `${i === 0 ? "This invoice" : "Subpayment"}: ${
                  r.advisor?.displayName ?? "Unassigned"
                } — ${formatPrice(r.payoutAmountCents, "USD")}${
                  r.payoutState === "paid" ? " (paid)" : ""
                }`,
            )
            .join("\n")}
        >
          <Chip
            variant="teal"
            icon={<Users size={16} />}
            label={`${subpaymentCount} subpayment${
              subpaymentCount > 1 ? "s" : ""
            }`}
            size="small"
          />
        </Tooltip>
      )}
      {isExtraHours && (
        <Chip
          variant="green"
          icon={<Timer size={16} />}
          label="Extra hours"
          color="warning"
          size="small"
        />
      )}
      {isLightningTask && (
        <>
          {stripeMetadata.subtask_ref ? (
            <LinkRouter
              to={`/tasks/subtasks/${encodeURIComponent(
                stripeMetadata.subtask_ref,
              )}`}
              sx={{
                padding: "0",
              }}
            >
              <Chip
                variant="teal"
                icon={<Zap size={16} />}
                label="Lightning task"
                size="small"
                sx={{
                  cursor: "pointer",
                }}
              />
            </LinkRouter>
          ) : (
            <Chip
              variant="teal"
              icon={<Zap size={16} />}
              label="Lightning task"
              size="small"
            />
          )}
          {stripeMetadata.multiple_completions === "true" && (
            <Chip
              variant="red"
              icon={<CircleAlert size={16} />}
              label="Task completed multiple times"
              size="small"
            />
          )}
          <LightningTaskTimeChip stripeMetadata={stripeMetadata} />
        </>
      )}
      {invoice.flags?.isAdvisorNew && (
        <Chip
          variant="green"
          icon={<Sparkles size={16} />}
          label="New Advisor"
          color="warning"
          size="small"
        />
      )}
      {!isExtraHours && utilization !== null && (
        <Chip
          variant={
            utilization > 0.5 ? "green" : utilization === 0 ? "red" : "orange"
          }
          icon={<Activity size={16} />}
          label={`${Math.round(utilization * 100)}% utilization`}
          color="warning"
          size="small"
        />
      )}

      {invoice.flags?.isRecentRematch && (
        <Chip
          variant="green"
          icon={<RefreshCcw size={16} />}
          label="Recent rematch"
          color="warning"
          size="small"
        />
      )}
      {!isExtraHours && !isLightningTask && invoice.serviceMonth <= 1 && (
        <Chip
          variant="green"
          icon={<Star size={16} />}
          label="First invoice"
          size="small"
        />
      )}
      {invoice.subtotalCents > invoice.invoiceAmountCents && (
        <Chip
          variant="green"
          icon={<Tag size={16} />}
          label="Discount applied"
          color="warning"
          size="small"
        />
      )}
      {invoice.flags?.isBillingChange && (
        <Chip
          variant="green"
          icon={<CreditCard size={16} />}
          label="Billing change"
          color="warning"
          size="small"
        />
      )}
      {invoice.flags?.advisorIsWrong && (
        <Chip
          variant="red"
          icon={<CircleAlert size={16} />}
          label="Advisor is wrong"
          color="warning"
          size="small"
        />
      )}
      {!invoice.isAdvisorPayable && (
        <Chip
          variant="red"
          icon={<CircleAlert size={16} />}
          label={
            invoice.advisor?.ref
              ? "Advisor is not linked to a bank account for payments."
              : "Invoice not associated with an advisor."
          }
          color="warning"
          size="small"
        />
      )}
    </Box>
  );
};

type RecipientDraft = {
  advisorRef: string | null;
  paymentDollars: number;
  invoiceRef?: string;
  payoutState?: string;
};

const draftsFromInvoice = (invoice: Invoice): RecipientDraft[] =>
  invoice.payoutRecipients.length > 0
    ? invoice.payoutRecipients.map((recipient) => ({
        advisorRef: recipient.advisorRef || null,
        paymentDollars: recipient.payoutAmountCents / 100,
        invoiceRef: recipient.invoiceRef,
        payoutState: recipient.payoutState,
      }))
    : [
        {
          advisorRef: invoice.advisor?.ref ?? null,
          paymentDollars: (invoice.paymentCents ?? 0) / 100,
        },
      ];

// -- Edit Invoice Modal
const EditInvoice = ({
  invoice,
  onClose,
  update,
  splitPayout,
  loading,
}: {
  invoice: Invoice;
  onClose: () => void;
  update: (data: PartialMessage<UpdateInvoiceRequest>) => void;
  splitPayout: (data: PartialMessage<SplitInvoicePayoutRequest>) => void;
  loading: boolean;
}) => {
  const [recipients, setRecipients] = useState<RecipientDraft[]>(() =>
    draftsFromInvoice(invoice),
  );
  const [changeNotes, setChangeNotes] = useState("");
  // A split payout is a child row. The server rejects splitting one, and its ceiling belongs to
  // the parent invoice, so this card only edits its own amount and recipient.
  const isSplitPayout = invoice.transactionType === "split-payout";

  const totalCents = recipients.reduce(
    (sum, r) =>
      sum +
      (Number.isFinite(r.paymentDollars)
        ? Math.round(r.paymentDollars * 100)
        : 0),
    0,
  );
  const overCeiling = !isSplitPayout && totalCents > invoice.subtotalCents;
  // Once an invoice has recipients it stays on the split path, so removing the second
  // recipient still archives their row rather than silently orphaning it.
  const isSplit = recipients.length > 1 || invoice.payoutRecipients.length > 0;
  // Only enforced when splitting. A lone invoice with no advisor is an existing valid state.
  const missingAdvisor = isSplit && recipients.some((r) => !r.advisorRef);
  const validationError = overCeiling
    ? `${formatPrice(totalCents, "USD")} is more than the ${formatPrice(
        invoice.subtotalCents,
        "USD",
      )} the client paid`
    : missingAdvisor
      ? "Every subpayment needs an advisor"
      : null;

  const patchRecipient = (index: number, patch: Partial<RecipientDraft>) =>
    setRecipients((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );

  const onSave = () => {
    if (validationError) return;
    if (isSplit) {
      splitPayout({
        invoiceRef: invoice.ref,
        recipients: recipients.map((r) => ({
          advisorRef: r.advisorRef ?? "",
          payoutAmountCents: Math.round(r.paymentDollars * 100),
        })),
        changeNotes,
      });
      return;
    }
    const [only] = recipients;
    const onlyCents = Math.round(only.paymentDollars * 100);
    // A subpayment's amount is deducted from its parent's payout, so it is resized from that
    // invoice rather than here. Only the recipient and notes are editable on this card.
    const amountUpdates = isSplitPayout
      ? {}
      : {
          setPaymentCentsZero:
            onlyCents !== invoice.paymentCents && only.paymentDollars === 0,
          paymentCents:
            onlyCents !== invoice.paymentCents ? onlyCents : undefined,
        };
    update({
      ...amountUpdates,
      changeNotes,
      advisorRef:
        only.advisorRef && only.advisorRef !== invoice.advisor?.ref
          ? only.advisorRef
          : undefined,
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px" padding="16px">
      {recipients.map((recipient, index) => {
        const isPaid = recipient.payoutState === "paid";
        return (
          <Box
            key={recipient.invoiceRef ?? `pending-${index}`}
            display="flex"
            flexDirection="column"
            gap="8px"
          >
            {recipients.length > 1 && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="bodySmall" color="text.secondary">
                  {index === 0 ? "This invoice" : `Subpayment ${index}`}
                </Typography>
                {isPaid ? (
                  <Chip variant="green" label="Paid" size="small" />
                ) : (
                  index > 0 && (
                    <IconButton
                      size="small"
                      aria-label="remove recipient"
                      onClick={() =>
                        setRecipients((prev) =>
                          prev.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  )
                )}
              </Box>
            )}
            <AdvisorSelect
              isAdmin={true}
              disabled={isPaid}
              selectedAdvisor={recipient.advisorRef}
              title={"Payment recipient"}
              onChange={(ref) => patchRecipient(index, { advisorRef: ref })}
            />
            <TextField
              label="Payment"
              type="number"
              disabled={isPaid || isSplitPayout}
              helperText={
                isSplitPayout
                  ? "Set from the invoice this subpayment was carved out of."
                  : undefined
              }
              value={recipient.paymentDollars}
              onChange={(e) =>
                patchRecipient(index, {
                  paymentDollars: parseFloat(e.target.value),
                })
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      margin: "0 0 0 20px",
                      paddingTop: "16px",
                    }}
                  >
                    $
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-input": {
                  padding: "0 0 0 6px",
                },
              }}
            />
          </Box>
        );
      })}

      {!isSplitPayout && (
        <Button
          variant="text"
          startIcon={<Plus size={16} />}
          onClick={() =>
            setRecipients((prev) => [
              ...prev,
              { advisorRef: null, paymentDollars: 0 },
            ])
          }
        >
          Add a subpayment
        </Button>
      )}

      {(recipients.length > 1 || validationError) && (
        <Typography
          variant="bodySmall"
          color={validationError ? "error.main" : "text.secondary"}
        >
          {validationError ??
            `${formatPrice(totalCents, "USD")} of ${formatPrice(
              invoice.subtotalCents,
              "USD",
            )}`}
        </Typography>
      )}

      <TextField
        label="Change Notes"
        multiline
        rows={4}
        value={changeNotes}
        onChange={(e) => setChangeNotes(e.target.value)}
        fullWidth
      />
      <Box justifyContent="end" display="flex" flexDirection="row" gap="8px">
        <Button onClick={onClose} variant="text" disabled={loading}>
          Close
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={loading || !!validationError}
        >
          {loading ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

// Add debounce utility
const debounce = (fn: Function, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

// -- Single Invoice Card
export default ({
  invoice,
  refresh,
  setAdvisorRef,
  setFamilyRef,
  onHeightChange,
  actionsDisabled,
  setActionsDisabled,
  isExpanded,
  onExpandedChange,
}: {
  invoice: Invoice;
  refresh: () => Promise<void>;
  setAdvisorRef: (r: string) => void;
  setFamilyRef: (r: string) => void;
  onHeightChange?: (height: number) => void;
  actionsDisabled: boolean;
  setActionsDisabled: (disabled: boolean) => void;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = isExpanded !== undefined ? isExpanded : internalExpanded;
  const setExpanded = (value: boolean) => {
    if (onExpandedChange) {
      onExpandedChange(value);
    } else {
      setInternalExpanded(value);
    }
  };
  const [viewMode, setViewMode] = useState<"edit" | "history">("history");
  const isSplitPayout = invoice.transactionType === "split-payout";
  const isMobile = useIsMobile();
  const [updateLock, setUpdateLock] = useState(false);
  const { pushSnack } = useSnackpack();
  const { request, loading } = useUpdateInvoice();
  const { request: splitRequest, loading: splitLoading } =
    useSplitInvoicePayout();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cardRef.current && onHeightChange) {
      // Create a debounced version of the height update
      const debouncedHeightUpdate = debounce((height: number) => {
        onHeightChange(height);
      }, 10); // 100ms debounce time

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const style = window.getComputedStyle(entry.target);
          const marginTop = parseFloat(style.marginTop);
          const marginBottom = parseFloat(style.marginBottom);
          debouncedHeightUpdate(
            entry.contentRect.height + marginTop + marginBottom,
          );
        }
      });
      resizeObserver.observe(cardRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [onHeightChange]);

  const update = async (data: PartialMessage<UpdateInvoiceRequest>) => {
    if (loading || updateLock || actionsDisabled) return;
    setUpdateLock(true);
    setActionsDisabled(true);
    await request(
      new UpdateInvoiceRequest({
        invoiceRef: invoice.ref,
        ...data,
        skipPaymentApproval: true,
      }),
    );
    pushSnack("Invoice updated");
    await refresh();
    setActionsDisabled(false);
    setUpdateLock(false);
  };

  const splitPayout = async (
    data: PartialMessage<SplitInvoicePayoutRequest>,
  ) => {
    if (splitLoading || updateLock || actionsDisabled) return;
    setUpdateLock(true);
    setActionsDisabled(true);
    const response = await splitRequest(new SplitInvoicePayoutRequest(data));
    if (response) {
      pushSnack("Subpayments updated");
      await refresh();
    }
    setActionsDisabled(false);
    setUpdateLock(false);
  };

  const byline = (
    <Box display={"flex"} flexDirection={"column"}>
      {/* Family name & links */}
      <Box display="flex" flexDirection="row" alignItems="center" gap="4px">
        <Typography
          variant="bodyHeavy"
          sx={{ cursor: "pointer" }}
          color="text.primary"
          onClick={(e) => {
            e.stopPropagation();
            if (invoice.family?.ref) {
              setFamilyRef(invoice.family.ref);
            }
          }}
        >
          {invoice.family?.name}
        </Typography>
        <Typography variant="body">
          {isSplitPayout ? "subpayment" : "paid"}
        </Typography>
        {!isSplitPayout && (
          <LinkRouter
            targetNew={true}
            to={`https://dashboard.stripe.com/invoices/${invoice.stripeId}`}
            sx={{
              alignItems: "center",
            }}
          >
            <Tooltip
              title={`${formatPrice(
                invoice.subtotalCents,
                invoice.invoiceCurrency,
              )}`}
            >
              <Typography variant="body">
                {formatPrice(
                  invoice.invoiceAmountCents,
                  invoice.invoiceCurrency,
                )}
                {invoice.subtotalCents !== invoice.invoiceAmountCents
                  ? "*"
                  : ""}
              </Typography>
            </Tooltip>
          </LinkRouter>
        )}
      </Box>

      {invoice.periodStartSec && (
        <Typography variant="bodySmall" color="text.secondary">
          {`${formatDateRange(invoice.periodStartSec, invoice.periodEndSec)}`}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      ref={cardRef}
      display="flex"
      flexDirection="column"
      sx={{
        borderRadius: "12px",
        border: "1px solid #EAEBEC",
        width: "100%",
        marginBottom: "16px",
        opacity: actionsDisabled ? 0.5 : 1,
        pointerEvents: actionsDisabled ? "none" : "auto",
      }}
    >
      <Box
        sx={{
          cursor: !expanded ? "pointer" : undefined,
          padding: "20px 24px",
        }}
      >
        <Box sx={{ display: "flex", gap: "8px" }}>
          {/* External links */}
          <FlexBox
            direction="column"
            justifyContent="start"
            alignItems="center"
            gap="6px"
            sx={{ height: "100%" }}
          >
            <LinkRouter
              to={`/tools/advisor-administration/${encodeURIComponent(
                invoice.advisor?.ref ?? "",
              )}`}
              sx={{ padding: "0" }}
            >
              <UserRoundCheck size={20} color="#8E9598" />
            </LinkRouter>
            <LinkRouter
              to={`/families/${invoice.family?.ref}?adminView=1#tab-admin`}
              sx={{ padding: "0" }}
            >
              <FamilyIcon width="22px" height="22px" fill="#8E9598" />
            </LinkRouter>
          </FlexBox>

          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "start" : "center"}
            gap="12px"
            width="100%"
            onClick={() => !expanded && setExpanded(true)}
          >
            {/* Invoice info */}
            <Box flex="5">
              <Box display="flex" flexDirection={"column"}>
                <Box
                  display="flex"
                  flexDirection={isMobile ? "column" : "row"}
                  gap="4px"
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="4px"
                  >
                    <Typography
                      variant="h4"
                      color="#262626"
                      sx={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (invoice.advisor?.ref) {
                          setAdvisorRef(invoice.advisor.ref);
                        }
                      }}
                    >
                      {`${invoice.advisor?.firstName} ${invoice.advisor?.lastName}`
                        .length <= 12
                        ? `${invoice.advisor?.firstName} ${invoice.advisor?.lastName}`
                        : `${invoice.advisor?.firstName} ${invoice.advisor?.lastName[0]}.`}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="body"
                  color="text.secondary"
                  sx={{ textWrap: "none" }}
                >
                  {byline}
                </Typography>
              </Box>
            </Box>

            {/* Service months */}
            <Box flex="1" display="flex" gap="4px" alignItems="center">
              {isMobile && (
                <Typography variant="bodyHeavy">Service months:</Typography>
              )}
              <Tooltip
                title={
                  invoice.family?.startDate &&
                  `Started ${shortDate(invoice.family?.startDate)}`
                }
              >
                <Typography variant="body" color="text">
                  {invoice.serviceMonth}
                </Typography>
              </Tooltip>
            </Box>

            {/* Percentage */}
            <Box
              flex="1"
              display="flex"
              gap="4px"
              alignItems={isMobile ? "start" : "center"}
            >
              {isMobile && <Typography variant="bodyHeavy">Fee:</Typography>}
              {/* A split payout carries no revenue, so there is no fee to derive from it. */}
              <Tooltip
                title={
                  isSplitPayout
                    ? ""
                    : feeSchedule(invoice.family?.platformFeeSchedule)
                }
              >
                <Typography variant="body" color="text">
                  {isSplitPayout ? "—" : `${invoice.feePercent * 100}%`}
                </Typography>
              </Tooltip>
            </Box>

            {/* Amount */}
            <Box
              flex="2"
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Tooltip
                title={
                  isSplitPayout
                    ? "Subpayment"
                    : `${formatPrice(
                        invoice.subtotalCents,
                        invoice.invoiceCurrency,
                      )} - ${invoice.feePercent * 100}%`
                }
              >
                <Typography
                  variant="bodyHeavy"
                  color="text.primary"
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {formatPrice(invoice.paymentCents, "USD")}
                </Typography>
              </Tooltip>
              {invoice.invoiceAmountCents !== invoice.subtotalCents && (
                <Typography variant="bodySmall" color="text.tertiary">
                  {formatPrice(
                    invoice.subtotalCents - invoice.invoiceAmountCents,
                    invoice.invoiceCurrency,
                  )}{" "}
                  off
                </Typography>
              )}
            </Box>

            {/* Actions */}
            <Box flex="1" display="flex" alignItems="center" gap="8px">
              <Box onClick={(e) => e.stopPropagation()}>
                <Tooltip
                  title={
                    invoice.status === "pending_approval" &&
                    invoice.paymentCents === 0
                      ? "Payment is $0. Consider archiving or deferring instead."
                      : ""
                  }
                >
                  <span>
                    <InvoiceStatusSelect
                      loading={loading || updateLock}
                      disabled={actionsDisabled}
                      paymentDisabled={
                        invoice.status === "pending_approval" &&
                        (!invoice.isAdvisorPayable ||
                          invoice.paymentCents === 0)
                      }
                      status={invoice.status}
                      setStatus={(s) => update({ status: s })}
                      onEdit={() => {
                        setExpanded(true);
                        setViewMode("edit");
                      }}
                    />
                  </span>
                </Tooltip>
              </Box>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => {
                  setExpanded(!expanded);
                  setViewMode("history");
                }}
              >
                {expanded ? <ChevronUp /> : <ChevronDown />}
              </IconButton>
            </Box>
          </Box>
        </Box>
        <FlexBox justifyContent="space-between" alignItems="center">
          <InvoiceFlags invoice={invoice} />
        </FlexBox>
      </Box>

      {/* Collapsed content */}
      <Collapse in={expanded} timeout={1000}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            background: "white",
            padding: "0px 24px 16px",
          }}
        >
          {/* Change to an edit mode */}
          {viewMode === "edit" && (
            <EditInvoice
              invoice={invoice}
              onClose={() => setExpanded(false)}
              update={update}
              splitPayout={splitPayout}
              loading={loading || splitLoading}
            />
          )}
          {expanded && viewMode === "history" && invoice.family?.ref && (
            <InvoiceHistoryFeed invoice={invoice} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
